import { createMuiTheme } from '@material-ui/core/styles';

const palette = {
  text: {
    primary: '#FFF',
    secondary: '#8F9ABA'
  },
  pink: '#FF3266',
  teal: '#00B9A3',
  redDark: '#A52E1B',
  red: '#FF5126',
  redLight: '#FEB8B6',

  navyDark3: '#081726',
  navyDark2: '#0B1D33',
  navyDark1: '#0E2540',
  navy: '#30445C',
  navyLight1: '#5B76A0',
  navyLight2: '#8F9ABA',
  navyLight3: '#A0B8FF',
  gray: '#B3B3B3',
  grayLight1: '#E6E6E6',
  grayLight2: '#EEEEEE',

  lecture: {
    light: '#00B3D1',
    dark: '#00688b'
  },
  meltdown: {
    light: '#FF5126',
    dark: '#A52E1B'
  },
  quickPlay: {
    light: '#15CEAE',
    dark: '#038C7F'
  },
};

const typography = {
  useNextVariants: true,
  fontFamily: [
    'museo-sans',
    'Helvetica Neue', 
    'Tahoma', 
    'sans-serif'
  ].join(','),
  fontFamilyInputs: [
    'Vox',
    'Impact',
    'Tahoma',
    'sans-serif'
  ].join(','),
};

const breakpoints = {
  values: {
    xs: 0,
    sm: 576,
    md: 768,
    lg: 992,
    lgr: 1024,
    xl: 1200,
  },
};

const defaultTheme = createMuiTheme({breakpoints});

export default createMuiTheme({
  typography,
  palette,
  breakpoints,
  overrides: {
    MuiButton: {
      root: {
        
      },
      text: {
        '&:hover': {
        },
        '&$disabled': {
        }
      },
      contained: {
        '&$disabled': {
        }
      },
      containedPrimary: {
        fontSize: 15,
        '&:hover': {
        },
        '&$disabled': {
        }
      },
      outlinedPrimary: {
        border: '2px solid currentColor',
        fontSize: 15,
        backgroundColor: '#fff',
        transition: 'all .3s',
        '&:hover': {
          border: '2px solid currentColor',
        },
        '&$disabled': {
        }
      },
      textPrimary: {
        minWidth: 0,
        '&:hover': {
          backgroundColor: 'transparent',
        },
      },
    },
    MuiCheckbox: {
      root: {
        '& .MuiSvgIcon-root': {
          fontSize: 19,
        },
        '& ~ .MuiFormControlLabel-label': {
          fontSize: 16,
          fontFamily: typography.fontFamilyInputs,
          fontWeight: 600,
        }
      },
      colorPrimary: {
        '&$checked': {
        },
      }
    },
    MuiDialog: {
      root: {
        '& .MuiPaper-rounded': {
          borderRadius: 8,
          width: '100%'
        },
      },
    },
    MuiDialogTitle: {
      root: {
        padding: '30px 35px',
        fontSize: 20,
        '& .MuiTypography-root': {
          fontWeight: 600,
          letterSpacing: '.01em'
        },
      }
    },
    MuiDialogContent: {
      root: {
        padding: '0px 35px',
      }, 
    },
    MuiDialogContentText: {
      root: {
        marginBottom: 25,
        '& .MuiTypography-root': {
        },
      }
    },
    MuiDialogActions: {
      root: {
        padding: '10px 35px 30px 35px',
        '& .MuiButton-containedPrimary': {
          '&:hover': {
            textDecoration: 'none',
          }
        },
      },
    },
    MuiBackdrop: {
      root: {
        
      },
    },
    MuiFab: {
      root: {
        boxShadow: 'none',
        '&$disabled' : {
        }
      },
      primary: {
        fontWeight: 600,
        fontSize: 15,
        '&.MuiLink-root:hover, &:hover': {
          textDecoration: 'none',
          color: '#fff',
        },
      },
      sizeMedium: {
        height: 45,
        width: 45,
        fontSize: 14,
      },
    },
    MuiInputBase: {
      root: {
        background: '#fff',
        '& legend': {
          width: 0,
        },
      },
      input: {
        fontFamily: typography.fontFamilyInputs,
        background: '#fff',
        borderRadius: 4,
        fontWeight: 500,
        '&::placeholder': {
          fontStyle: 'italic'
        }
      },
    },
    MuiInput: {
      root: {
        background: '#fff',
      },
    },
    MuiInputLabel: {
      outlined: {
        transform: 'translate(14px, 33px) scale(1)',
        '&.MuiInputLabel-shrink': {
          transform: 'translate(0px, 0px) scale(0.75)'
        },
      },
      root: {
        fontFamily: typography.fontFamilyInputs,
        '&.Mui-focused': {
        },
      },
    },
    MuiOutlinedInput: {
      root: {
        marginTop: 20,
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        },
      },
      input: {
        padding: '10.5px 14px',
        '&::placeholder': {
          opacity: 1
        },
      },
      multiline: {
        padding: 14,
      }
    },
    MuiFormControl: {
      root: {
        color: '#707070'
      },
      fullWidth: {
      }
    },
    MuiFormHelperText: {
      root: {
        fontFamily: typography.fontFamilyInputs,
        lineHeight: '16px',
        '& a': {
          textDecoration: 'underline'
        }
      }
    },
    MuiFormLabel: {
      root: {
        fontFamily: typography.fontFamilyInputs,
        fontWeight: 500,
      },
    },
    MuiTooltip: {
      arrow: {
        color: '#DEE7FF',
      },
      tooltip: {
        borderRadius: 2,
        fontSize: '1.6rem',
        color: '#0D1B2D',
        backgroundColor: '#DEE7FF',
        fontWeight: 500,
        padding: '6px 1.6rem',
        textAlign: 'center',
        maxWidth: 'none'
      },
    },
    MuiTypography: {
      h4: {
        fontSize: '1.6rem',
        [defaultTheme.breakpoints.up('lg')]: {
          fontSize: '2.1rem'
        }
      }
    }
  },
  props: {
    MuiButton: {
      variant: 'contained',
      disableElevation: true
    },
    MuiFormHelperText: {
      variant: 'filled'
    },
    MuiTextField: {
      variant: 'outlined',
    },
    MuiTooltip: {
      arrow: true,
    },
  },
});